import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Message, SectionHeading } from "../components";
import { useSite } from "../../store";
import { normalizeTimeline } from "../../utils/timeline";
import { TimelineDay } from "./parts";
import { useCurrentEvent } from "./useCurrentEvent";
import "./Timeline.scss";

/** Parses a "YYYY-MM-DD" string into a local Date, or null. */
function parseDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * The wedding timeline page. Shows the schedule of events the couple set in
 * the admin, grouped by day (with a day header when there is more than one),
 * and highlights what is happening now on the day itself. Gated by the
 * `allowTimeline` permission (see App routing).
 */
export const Timeline = () => {
  const { t, i18n } = useTranslation();
  const { config } = useSite();

  const { days, timeZone, showDates } = useMemo(
    () => normalizeTimeline(config.timeline),
    [config.timeline],
  );
  const visibleDays = useMemo(
    () => days.filter((d) => d.events.length > 0),
    [days],
  );

  const { currentId, nextId } = useCurrentEvent(visibleDays, timeZone);

  // Show per-day headers only when the operator opted in and there is more than
  // one day; otherwise render everything as one continuous timeline.
  const grouped = showDates !== false && visibleDays.length > 1;
  const totalEvents = visibleDays.reduce((n, d) => n + d.events.length, 0);

  const dayHeader = (date: string, label?: string): string => {
    const parsed = parseDate(date);
    const datePart = parsed
      ? new Intl.DateTimeFormat(i18n.language, {
          weekday: "long",
          day: "numeric",
          month: "long",
        }).format(parsed)
      : "";
    const trimmed = label?.trim();
    if (datePart && trimmed) return `${datePart} · ${trimmed}`;
    return trimmed || datePart;
  };

  return (
    <div className="body">
      <SectionHeading
        title={t("timeline.title")}
        subtitle={t("timeline.subtitle")}
      />
      {totalEvents === 0 ? (
        <Message variant="primary">{t("timeline.empty")}</Message>
      ) : (
        <div className="timeline-days">
          {grouped ? (
            visibleDays.map((day) => (
              <TimelineDay
                key={day.id}
                events={day.events}
                header={dayHeader(day.date, day.label)}
                currentId={currentId}
                nextId={nextId}
              />
            ))
          ) : (
            <TimelineDay
              events={visibleDays.flatMap((d) => d.events)}
              header={null}
              currentId={currentId}
              nextId={nextId}
            />
          )}
        </div>
      )}
    </div>
  );
};
